import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  FileText, 
  Edit, 
  Trash2, 
  Filter,
  Search,
  Calendar,
  TrendingUp,
  RefreshCw,
  Eye
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { formatDate, formatPercentage } from '../utils/formatters';
import toast from 'react-hot-toast';

interface Document {
  id: string;
  filename: string;
  documentType: string;
  status: string;
  initialConfidenceScore: number;
  initialProcessingTime: number;
  createdAt: string;
  goldenDataValidatedAt: string | null;
  goldenDataChangesMade: number | null;
  initialAccuracy: number | null;
}

interface Pagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export const DocumentList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [retryProgress, setRetryProgress] = useState<{[key: string]: any}>({});

  const statusFilter = searchParams.get('status') || '';
  const typeFilter = searchParams.get('type') || '';

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);
      // Load all documents without pagination
      params.append('limit', '1000');
      params.append('offset', '0');

      const response = await apiClient.get(`/api/documents?${params.toString()}`);
      setDocuments(response.data.documents);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (documentId: string, filename: string) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    try {
      await apiClient.delete(`/api/documents/${documentId}`);
      toast.success('Document deleted successfully');
      fetchDocuments();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleRetry = async (documentId: string, filename: string) => {
    if (!window.confirm(`Chcete znovu zpracovat dokument "${filename}"?`)) {
      return;
    }

    try {
      const response = await apiClient.post(`/api/documents/${documentId}/retry`);
      const { retryId } = response.data;
      
      toast.success('Dokument byl odeslán ke zpracování');
      
      // Start listening to retry progress
      const eventSource = new EventSource(`${process.env.REACT_APP_API_URL}/api/documents/retry-progress/${retryId}`);
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'retry_progress') {
            setRetryProgress(prev => ({
              ...prev,
              [documentId]: data
            }));
            
            // Show completion message
            if (data.status === 'completed') {
              toast.success(`Dokument "${filename}" byl úspěšně zpracován!`);
              fetchDocuments();
              eventSource.close();
              
              // Clear progress after delay
              setTimeout(() => {
                setRetryProgress(prev => {
                  const newProgress = { ...prev };
                  delete newProgress[documentId];
                  return newProgress;
                });
              }, 3000);
            } else if (data.status === 'error') {
              toast.error(`Chyba při zpracování dokumentu "${filename}": ${data.error}`);
              fetchDocuments();
              eventSource.close();
              
              // Clear progress after delay
              setTimeout(() => {
                setRetryProgress(prev => {
                  const newProgress = { ...prev };
                  delete newProgress[documentId];
                  return newProgress;
                });
              }, 3000);
            }
          }
        } catch (error) {
          console.error('Error parsing retry progress:', error);
        }
      };
      
      eventSource.onerror = () => {
        eventSource.close();
      };
      
    } catch (error) {
      console.error('Retry error:', error);
      toast.error('Chyba při odesílání dokumentu ke zpracování');
    }
  };

  const handleCheckStatus = async (documentId: string) => {
    try {
      const response = await apiClient.get(`/api/documents/${documentId}/status`);
      const { document, lyxApiStatus } = response.data;
      
      let statusMessage = `Dokument: ${document.status}`;
      if (lyxApiStatus?.data?.processing_status) {
        statusMessage += `\nAPI status: ${lyxApiStatus.data.processing_status}`;
      }
      
      toast.success(statusMessage);
    } catch (error) {
      console.error('Status check error:', error);
      toast.error('Chyba při kontrole stavu dokumentu');
    }
  };

  const updateFilters = (newFilters: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    
    setSearchParams(newParams);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'golden':
        return <span className="badge-success">Golden</span>;
      case 'pending':
        return <span className="badge-warning">Čeká na validaci</span>;
      case 'processing':
        return <span className="badge bg-blue-100 text-blue-800">Zpracovává se</span>;
      case 'error':
        return <span className="badge-error">Chyba</span>;
      default:
        return <span className="badge-gray">{status}</span>;
    }
  };

  const getDocumentTypeBadge = (type: string) => {
    switch (type) {
      case 'invoice':
        return <span className="badge bg-blue-100 text-blue-800">Faktura</span>;
      case 'receipt':
        return <span className="badge bg-purple-100 text-purple-800">Doklad</span>;
      case 'unknown':
        return <span className="badge bg-gray-100 text-gray-600">Neznámý</span>;
      default:
        return <span className="badge-gray">{type}</span>;
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dokumenty</h1>
        <p className="text-gray-600 mt-2">
          Spravujte vaše nahráné dokumenty a jejich stav validace
        </p>
      </div>

      {/* Filters and Search */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Hledat dokumenty..."
              className="input pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <div>
            <select
              className="input"
              value={statusFilter}
              onChange={(e) => updateFilters({ status: e.target.value })}
            >
              <option value="">Všechny stavy</option>
              <option value="pending">Čeká na validaci</option>
              <option value="golden">Golden Data</option>
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <select
              className="input"
              value={typeFilter}
              onChange={(e) => updateFilters({ type: e.target.value })}
            >
              <option value="">Všechny typy</option>
              <option value="invoice">Faktury</option>
              <option value="receipt">Doklady</option>
            </select>
          </div>

          {/* Clear Filters */}
          <div>
            <button
              onClick={() => setSearchParams({})}
              className="btn-secondary w-full"
              disabled={!statusFilter && !typeFilter}
            >
              <Filter className="h-4 w-4 mr-2" />
              Vymazat filtry
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {pagination && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="card py-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{pagination.total}</div>
              <div className="text-sm text-gray-500">Celkem dokumentů</div>
            </div>
          </div>
          <div className="card py-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {documents.filter(d => d.status === 'golden').length}
              </div>
              <div className="text-sm text-gray-500">Golden Data</div>
            </div>
          </div>
          <div className="card py-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {documents.filter(d => d.status === 'pending').length}
              </div>
              <div className="text-sm text-gray-500">Čeká na validaci</div>
            </div>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="card">
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {documents.length === 0 ? 'Zatím nebyl nahrán žádný dokument' : 'Žádné dokumenty neodpovídají vašemu vyhledávání'}
            </p>
            {documents.length === 0 && (
              <Link to="/upload" className="btn-primary mt-4">
                Nahrát dokumenty
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Dokument</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Typ</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Stav</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Jistota</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Přesnost</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Nahráno</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-900">Akce</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.map((document) => (
                  <tr key={document.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{document.filename}</p>
                          <p className="text-sm text-gray-500">Zpracování: {document.initialProcessingTime}ms</p>
                          
                          {/* Show retry progress */}
                          {retryProgress[document.id] && (
                            <div className="mt-2">
                              <div className="flex items-center space-x-2">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${retryProgress[document.id].progress}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-gray-600">
                                  {retryProgress[document.id].progress}%
                                </span>
                              </div>
                              <p className="text-xs text-blue-600 mt-1">
                                {retryProgress[document.id].step}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {getDocumentTypeBadge(document.documentType)}
                    </td>
                    <td className="py-4 px-4">
                      {getStatusBadge(document.status)}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="h-4 w-4 text-gray-400" />
                        <span>{document.initialConfidenceScore}%</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {document.initialAccuracy !== null ? (
                        <span className={`font-medium ${
                          document.initialAccuracy >= 90 ? 'text-green-600' :
                          document.initialAccuracy >= 70 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {formatPercentage(document.initialAccuracy)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {formatDate(document.createdAt)}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        {document.status === 'pending' ? (
                          <Link
                            to={`/documents/${document.id}/validate`}
                            className="btn-primary text-sm"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Validovat
                          </Link>
                        ) : (
                          <Link
                            to={`/documents/${document.id}/validate`}
                            className="btn-secondary text-sm"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Upravit
                          </Link>
                        )}
                        
                        {/* Retry button for error/processing status */}
                        {(document.status === 'error' || document.status === 'processing') && (
                          <button
                            onClick={() => handleRetry(document.id, document.filename)}
                            className="btn-warning text-sm"
                            title="Znovu zpracovat"
                            disabled={retryProgress[document.id] && retryProgress[document.id].status !== 'completed' && retryProgress[document.id].status !== 'error'}
                          >
                            <RefreshCw className={`h-4 w-4 ${retryProgress[document.id] && retryProgress[document.id].status !== 'completed' && retryProgress[document.id].status !== 'error' ? 'animate-spin' : ''}`} />
                          </button>
                        )}
                        
                        {/* Status check button for processing documents */}
                        {document.status === 'processing' && (
                          <button
                            onClick={() => handleCheckStatus(document.id)}
                            className="btn-info text-sm"
                            title="Zkontrolovat stav"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleDelete(document.id, document.filename)}
                          className="btn-error text-sm"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Show total count instead of pagination */}
      {documents.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-500">
          Zobrazeno {documents.length} dokumentů
        </div>
      )}
    </div>
  );
};